import { createPortal } from 'react-dom';
import type { DayReviewDto } from '../../api/types.js';
import styles from './DayExportView.module.css';

export interface DayExportViewProps {
  classroomName: string;
  dateLabel: string;
  review: DayReviewDto | null;
}

const has = (s: string) => s.trim().length > 0;

/**
 * A clean, print-only record of one day's review. Hidden on screen; an
 * `@media print` block makes this the only visible element so the teacher can
 * Print / Save-as-PDF a shareable summary (sub notes, parent, records).
 */
export function DayExportView({ classroomName, dateLabel, review }: DayExportViewProps) {
  if (!review) return null;
  return createPortal(
    <div className={styles.exportRoot} data-testid="day-export">
      <header className={styles.head}>
        <h1 className={styles.title}>{classroomName}</h1>
        <p className={styles.date}>{dateLabel}</p>
      </header>

      {has(review.message) ? (
        <section className={styles.section}>
          <h2 className={styles.h2}>Message</h2>
          <p className={styles.body}>{review.message}</p>
        </section>
      ) : null}

      {has(review.questionDaily) ? (
        <section className={styles.section}>
          <h2 className={styles.h2}>Question of the day</h2>
          <p className={styles.body}>{review.questionDaily}</p>
          {has(review.questionDailyAnswer) ? (
            <p className={styles.answer}>Answer: {review.questionDailyAnswer}</p>
          ) : null}
        </section>
      ) : null}

      {review.weather ? (
        <section className={styles.section}>
          <h2 className={styles.h2}>Weather</h2>
          <p className={styles.body}>
            {[
              review.weather.kind,
              review.weather.temperature != null ? `${review.weather.temperature}°` : null,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
        </section>
      ) : null}

      {review.todo.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.h2}>To-Do</h2>
          <ul className={styles.list}>
            {review.todo.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {review.turnIn.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.h2}>Turn-In</h2>
          <ul className={styles.list}>
            {review.turnIn.map((t) => (
              <li key={t.subjectId}>
                <strong>{t.subjectName}:</strong> {t.assignment}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {review.searches.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.h2}>Searches</h2>
          <ul className={styles.list}>
            {review.searches.map((s) => (
              <li key={s.id}>
                {s.kind === 'youtube' ? '▶ ' : '🔍 '}
                {s.query} <span className={styles.muted}>({s.engine})</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>,
    document.body,
  );
}
